import React, { useCallback, useMemo, useState } from 'react';

import { isUUIDV4 } from '@aline/common';
import type { AutocompleteRenderInputParams } from '@mui/material';
import { Autocomplete, Badge, Chip, Menu, TextField, Typography } from '@mui/material';
import {
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { Settings2 } from 'lucide-react';
import type { DisplayMode } from 'src/components';
import { DropdownSelectButton } from 'src/components';
import { Apollo } from 'src/gql/generated';
import type { GroupableColumn } from 'src/hooks';
import { useGroupableColumns } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useDisplayMenuStyles = makeStyles()(theme => ({
  chip: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    fontFamily: theme.typography.fontFamily,
    fontSize: 10,
    fontWeight: 600,
    height: 24,
  },
  chipHidden: {
    opacity: 0.5,
  },
  columnList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2),
    width: 300,
  },
  displayColumnsHeader: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  groupingCategoryHeader: {
    backgroundColor: theme.appColors.background,
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: '32px',
  },
  groupingLevel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  groupingLevelLabel: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    width: 64,
  },
  groupingPicker: {
    flex: 1,
  },
  groupingSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    width: 340,
  },
}));

/**
 * A column the menu can list. The grouping picker's options and the display
 * columns chips want the same pair, so they share the grouping hook's shape.
 */
type ColumnOption = GroupableColumn;

/**
 * Sentinel for the relationship tree, which shares the grouping picker but is a
 * display mode rather than a column. Not a valid column field, so it can never
 * collide with one.
 */
const RELATIONSHIP_OPTION_FIELD = '__relationship__';

/**
 * Sentinel for the disabled row that says AI properties are still loading.
 * Like the relationship one, it is not a valid column field.
 */
const LOADING_OPTION_FIELD = '__loading__';

/** Category headings in the grouping picker. `getGroupingOptions` sets their order. */
const OPTION_CATEGORY = {
  documentField: 'Document fields',
  extraction: 'AI properties',
  layout: 'Layout',
  unavailable: 'Unavailable',
} as const;

type OptionCategory = (typeof OPTION_CATEGORY)[keyof typeof OPTION_CATEGORY];

/** The order `getGroupingOptions` sorts categories into; see the note there. */
const CATEGORY_ORDER: OptionCategory[] = [
  OPTION_CATEGORY.layout,
  OPTION_CATEGORY.documentField,
  OPTION_CATEGORY.extraction,
  OPTION_CATEGORY.unavailable,
];

/**
 * How many levels deep the picker offers to group. A picker-only ceiling — the
 * server groups at whatever depth it is handed — set here because every level
 * of an expansion path costs another key lookup per row over the filtered set,
 * and a fourth level is far likelier to be a misclick than a view anyone reads.
 */
const MAX_GROUPING_LEVELS = 3;

/** Stable default so an unset `rowGroupingModel` doesn't remount the pickers. */
const EMPTY_ROW_GROUPING_MODEL: readonly string[] = [];

const DEFAULT_DISPLAYABLE_COLUMNS: ColumnOption[] = [
  { field: Apollo.ReportColumnFileField.Status, label: 'Status' },
  { field: Apollo.ReportColumnFileField.Type, label: 'Type' },
  { field: Apollo.ReportColumnFileField.Parties, label: 'Parties' },
  { field: Apollo.ReportColumnFileField.Labels, label: 'Labels' },
  { field: Apollo.ReportColumnFileField.CreatedAt, label: 'Created At' },
];

export interface GroupingOption extends GroupableColumn {
  category: OptionCategory;
}

/**
 * Listed under "AI properties" while the page is still fetching the org's
 * questions. MUI's own `loading` text only shows when the list is empty, and
 * this list always holds Relationships and the document fields, so without
 * this row the section is just missing until the questions arrive.
 */
const LOADING_GROUPING_OPTION: GroupingOption = {
  category: OPTION_CATEGORY.extraction,
  field: LOADING_OPTION_FIELD,
  label: 'Loading AI properties…',
};

/**
 * Every choice the grouping pickers share. Exported for tests.
 *
 * MUI's `groupBy` opens a new heading whenever consecutive options disagree
 * on category, so the list has to arrive grouped by category: one split
 * across two runs renders its heading twice. `CATEGORY_ORDER` is that order,
 * and the sort is stable, so columns keep the grid's own order within a
 * category and the loading row comes after any question already listed.
 */
export function getGroupingOptions({
  groupableColumns,
  loading,
}: {
  groupableColumns: readonly GroupableColumn[];
  loading: boolean;
}): GroupingOption[] {
  const options: GroupingOption[] = [
    {
      category: OPTION_CATEGORY.layout,
      field: RELATIONSHIP_OPTION_FIELD,
      label: 'Relationships',
    },
    ...groupableColumns.map(column => ({
      ...column,
      // A question column is identified by its UUID field, as on the server.
      category: isUUIDV4(column.field) ? OPTION_CATEGORY.extraction : OPTION_CATEGORY.documentField,
    })),
    ...(loading ? [LOADING_GROUPING_OPTION] : []),
  ];

  return options.sort(
    (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
  );
}

/** Only the loading row is disabled. Exported for tests. */
export function getIsGroupingOptionDisabled(option: GroupingOption): boolean {
  return option.field === LOADING_OPTION_FIELD;
}

/**
 * Stand-in for a grouping level whose column the grid doesn't offer — a
 * question column deleted since the report was saved, or any column while the
 * definitions are still loading.
 *
 * Without it the level renders as an empty "None" picker with no clear control
 * while still counting toward the grouping, so the only way out is to pick a
 * column — which lands at the *next* level and leaves the unknown field pinned
 * above it. Offering the field as its own option keeps the level visible,
 * replaceable and clearable.
 */
function getUnresolvedGroupingOption(field: string | undefined): GroupingOption | null {
  if (!field) return null;

  return {
    category: OPTION_CATEGORY.unavailable,
    field,
    label: isUUIDV4(field) ? 'Unavailable AI property' : `Unavailable column (${field})`,
  };
}

/**
 * What one grouping level shows and what it offers. Exported for tests; the
 * menu calls it once per rendered level.
 */
export function getGroupingLevelState({
  groupingOptions,
  level,
  rowGroupingModel,
  selectedField,
}: {
  groupingOptions: GroupingOption[];
  level: number;
  rowGroupingModel: readonly string[];
  selectedField: string | undefined;
}): { options: GroupingOption[]; value: GroupingOption | null } {
  const value =
    groupingOptions.find(option => option.field === selectedField) ??
    getUnresolvedGroupingOption(selectedField);
  /**
   * Relationships only makes sense as the whole layout, and a column already
   * used at another level would produce subgroups that each hold exactly their
   * parent's rows.
   */
  const options = groupingOptions.filter(
    option =>
      (option.field !== RELATIONSHIP_OPTION_FIELD || level === 0) &&
      (option.field === selectedField || !rowGroupingModel.includes(option.field))
  );
  // An unresolved value must be offered too, or MUI reports the value as invalid.
  const isValueOffered = !value || options.includes(value);

  return { options: isValueOffered ? options : [...options, value], value };
}

interface GroupingLevelPickerProps {
  groupingOptions: GroupingOption[];
  level: number;
  onChange: (level: number, option: GroupingOption | null) => void;
  rowGroupingModel: readonly string[];
  selectedField: string | undefined;
}

/**
 * One level of the grouping path. Memoized because the menu re-renders on every
 * column-visibility toggle below it, and each picker holds an Autocomplete with
 * its own option list; the props it takes are stable across those renders.
 */
const GroupingLevelPicker = React.memo(function GroupingLevelPicker({
  groupingOptions,
  level,
  onChange,
  rowGroupingModel,
  selectedField,
}: GroupingLevelPickerProps) {
  const { classes } = useDisplayMenuStyles();
  const { options, value } = useMemo(
    () => getGroupingLevelState({ groupingOptions, level, rowGroupingModel, selectedField }),
    [groupingOptions, level, rowGroupingModel, selectedField]
  );
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, option: GroupingOption | null) => onChange(level, option),
    [level, onChange]
  );
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder="None"
        size="small"
        slotProps={{
          ...params.slotProps,
          /**
           * The visible label reads "then by" at every level past the first, so
           * the level number is what separates one combobox from the next for a
           * screen reader.
           */
          htmlInput: {
            ...params.slotProps.htmlInput,
            'aria-label': level === 0 ? 'Group by' : `Then group by (level ${level + 1})`,
          },
        }}
      />
    ),
    [level]
  );

  return (
    <div className={classes.groupingLevel}>
      <Typography className={classes.groupingLevelLabel} variant="body2">
        {level === 0 ? 'Group by' : 'then by'}
      </Typography>
      <Autocomplete
        className={classes.groupingPicker}
        classes={{ groupLabel: classes.groupingCategoryHeader }}
        getOptionDisabled={getIsGroupingOptionDisabled}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        groupBy={getOptionCategory}
        isOptionEqualToValue={getIsSameOption}
        options={options}
        renderInput={renderInput}
        size="small"
        value={value}
        onChange={handleChange}
      />
    </div>
  );
});

function getOptionLabel(option: GroupingOption): string {
  return option.label;
}

/**
 * MUI keys each listed option on its label unless told otherwise, and two AI
 * properties can carry the same title. The field is what sets them apart.
 */
function getOptionKey(option: GroupingOption): string {
  return option.field;
}

function getOptionCategory(option: GroupingOption): string {
  return option.category;
}

function getIsSameOption(option: GroupingOption, selected: GroupingOption): boolean {
  return option.field === selected.field;
}

/**
 * One picker per active grouping level, plus a trailing empty one to add the
 * next, up to the cap. The picker cannot build a model past the cap, but a
 * report saved through the API can carry one, and so can a report saved before
 * a lower cap. Every level of such a model still gets a picker, so the extra
 * ones are visible and can be cleared; it just gets no empty one to add more.
 */
export function getGroupingLevelCount(rowGroupingModel: readonly string[]): number {
  if (rowGroupingModel.length >= MAX_GROUPING_LEVELS) return rowGroupingModel.length;
  return rowGroupingModel.length + 1;
}

export interface DisplayMenuProps {
  displayableColumns?: ColumnOption[];
  displayMode?: DisplayMode;
  /** Defaults to every groupable column on the grid — see `useGroupableColumns`. */
  groupableColumns?: GroupableColumn[];
  /** True while some groupable columns, such as the org's questions, are still loading. */
  groupableColumnsLoading?: boolean;
  onDisplayModeChange?: (mode: DisplayMode) => void;
  onRowGroupingModelChange?: (model: string[]) => void;
  rowGroupingModel?: readonly string[];
}

export function DisplayMenu({
  displayMode = 'flat',
  displayableColumns = DEFAULT_DISPLAYABLE_COLUMNS,
  groupableColumns,
  groupableColumnsLoading = false,
  onDisplayModeChange,
  onRowGroupingModelChange,
  rowGroupingModel = EMPTY_ROW_GROUPING_MODEL,
}: DisplayMenuProps) {
  const apiRef = useGridApiContext();
  const { classes } = useDisplayMenuStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const gridGroupableColumns = useGroupableColumns();
  const resolvedGroupableColumns = groupableColumns ?? gridGroupableColumns;

  const isRelationshipMode = displayMode === 'relationship';
  const activeGroupingCount = isRelationshipMode ? 1 : rowGroupingModel.length;

  const groupingOptions = useMemo(
    () =>
      getGroupingOptions({
        groupableColumns: resolvedGroupableColumns,
        loading: groupableColumnsLoading,
      }),
    [groupableColumnsLoading, resolvedGroupableColumns]
  );

  /**
   * Relationship mode replaces grouping entirely, so it shows a single picker
   * holding the Relationships option.
   */
  const levelCount = isRelationshipMode ? 1 : getGroupingLevelCount(rowGroupingModel);

  /**
   * Clearing a level (via the picker's clear control) removes it and promotes
   * the levels below, which also exits relationship mode back to a flat list.
   */
  const handleLevelChange = useCallback(
    (level: number, option: GroupingOption | null) => {
      if (option?.field === RELATIONSHIP_OPTION_FIELD) {
        onDisplayModeChange?.('relationship');
        onRowGroupingModelChange?.([]);
        return;
      }

      const nextModel = [...rowGroupingModel];
      if (option) {
        nextModel[level] = option.field;
      } else {
        nextModel.splice(level, 1);
      }

      onDisplayModeChange?.('flat');
      onRowGroupingModelChange?.(nextModel);
    },
    [onDisplayModeChange, onRowGroupingModelChange, rowGroupingModel]
  );

  const isColumnVisible = (field: string) => columnVisibilityModel[field] !== false;

  const toggleColumn = (field: string) => {
    apiRef.current.setColumnVisibilityModel({
      ...columnVisibilityModel,
      [field]: !isColumnVisible(field),
    });
  };

  return (
    <>
      <Badge
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        badgeContent={activeGroupingCount}
        color="primary"
        sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}
      >
        <DropdownSelectButton
          ref={setAnchorEl}
          buttonVariant="default"
          icon={<Settings2 size={18} />}
          label="Display"
          selected={menuOpen}
          variant="outlined"
          onClick={() => setMenuOpen(!menuOpen)}
        />
      </Badge>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setMenuOpen(false)}
      >
        <div className={classes.groupingSection}>
          <Typography variant="subtitle2">Grouping</Typography>
          {Array.from({ length: levelCount }, (_, level) => (
            <GroupingLevelPicker
              key={level}
              groupingOptions={groupingOptions}
              level={level}
              rowGroupingModel={rowGroupingModel}
              selectedField={
                isRelationshipMode ? RELATIONSHIP_OPTION_FIELD : rowGroupingModel[level]
              }
              onChange={handleLevelChange}
            />
          ))}
        </div>

        <Typography className={classes.displayColumnsHeader} variant="subtitle2">
          Display columns
        </Typography>
        <div className={classes.columnList}>
          {displayableColumns.map(col => (
            <Chip
              key={col.field}
              clickable
              className={clsx(classes.chip, !isColumnVisible(col.field) && classes.chipHidden)}
              color="default"
              label={col.label}
              variant={isColumnVisible(col.field) ? 'filled' : 'outlined'}
              onClick={() => toggleColumn(col.field)}
            />
          ))}
        </div>
      </Menu>
    </>
  );
}
